import { useEffect, useRef } from "react"
import { useTranslation } from "react-i18next"

import { MOTION_BEATS } from "@/lib/motion"
import { cn } from "@/lib/utils"

interface RevealBannerProps {
  campaignLabel: string
  /** When true, moves focus to the heading on mount (the in-place flip moves
   *  focus here per UX Spec §6). */
  focusOnMount?: boolean
  className?: string
}

/**
 * OKS-477 — §07 reveal banner (Figma `47:261`). The celebratory "🎉 IT'S
 * HERE" + "The {campaign} just dropped" header shown once a drop fires.
 *
 * Plays the one-time `drop-reveal` beat (opacity 0→1 + scale 0.98→1, ≤480ms,
 * CSS-only, reduced-motion-safe via `motion-safe:`). When the flip happens in
 * place, focus moves to the heading (UX Spec §6) via a ref-driven effect.
 */
export function RevealBanner({
  campaignLabel,
  focusOnMount = false,
  className,
}: RevealBannerProps) {
  const { t } = useTranslation()
  const headingRef = useRef<HTMLHeadingElement>(null)

  useEffect(() => {
    if (focusOnMount) headingRef.current?.focus()
  }, [focusOnMount])

  return (
    <section
      className={cn(
        "rounded-2xl bg-[#ff5c00] px-5 py-6 text-center text-white shadow-sm",
        MOTION_BEATS["drop-reveal"],
        className,
      )}
      data-testid="reveal-banner"
    >
      <h1
        ref={headingRef}
        tabIndex={-1}
        className="text-2xl font-extrabold outline-none"
        data-testid="reveal-banner-title"
      >
        {t("mission_drop.reveal.title")}
      </h1>
      <p className="mt-1 text-sm font-medium opacity-95">
        {t("mission_drop.reveal.subtitle", { campaign: campaignLabel })}
      </p>
    </section>
  )
}
